
import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { User, UserRole } from '../types';

interface AddUserModalProps {
  companyId: string;
  onClose: () => void;
  onSubmit: (userData: Omit<User, 'id'> & { password?: string }) => Promise<void> | void;
}

const AddUserModal: React.FC<AddUserModalProps> = ({ companyId, onClose, onSubmit }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSystemContext = companyId === 'system';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: isSystemContext ? UserRole.SUPPORT_USER : UserRole.USER as UserRole
  });

  const [isMounted, setIsMounted] = useState(true);
  useEffect(() => {
    return () => setIsMounted(false);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.password || isSubmitting) return;

    setIsSubmitting(true);
    
    try {
      await Promise.race([
        onSubmit({
          ...formData,
          companyId: companyId,
        }),
        new Promise(resolve => setTimeout(resolve, 1500))
      ]);
      
      if (isMounted) onClose();
    } catch (e) {
      console.error(e);
      if (isMounted) onClose();
    } finally {
      if (isMounted) setIsSubmitting(false);
    }
  };

  const modalContent = (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-6 orion-user-modal-backdrop animate-in fade-in duration-300">
      <div className="absolute inset-0" onClick={!isSubmitting ? onClose : undefined}></div>
      <div className="relative orion-user-modal-container w-full max-w-lg rounded-[3rem] p-10 animate-in zoom-in-95 max-h-[90vh] overflow-y-auto custom-scrollbar">
        <h3 className="text-2xl font-black mb-2 text-white tracking-tight">
            {isSystemContext ? 'Ajouter Membre Support' : 'Ajouter Collaborateur'}
        </h3>
        <p className="text-slate-400 mb-8 text-sm">Créez un accès pour un nouveau membre.</p>
        
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Nom Complet</label>
            <input
              required
              type="text"
              value={formData.name}
              onChange={e => setFormData({...formData, name: e.target.value})}
              placeholder="ex: Marie Curie"
              className="w-full orion-user-input rounded-2xl px-5 py-4 text-sm font-bold"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Email (Identifiant)</label>
            <input
              required
              type="email"
              value={formData.email}
              onChange={e => setFormData({...formData, email: e.target.value})}
              placeholder="marie@entreprise.com"
              className="w-full orion-user-input rounded-2xl px-5 py-4 text-sm font-bold"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Mot de passe</label>
            <input
              required
              type="password"
              value={formData.password}
              onChange={e => setFormData({...formData, password: e.target.value})}
              placeholder="••••••••"
              className="w-full orion-user-input rounded-2xl px-5 py-4 text-sm font-bold"
            />
          </div>
          <div>
            <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Rôle & Permissions</label>
            <div className="grid grid-cols-2 gap-3">
               {isSystemContext ? (
                 <>
                    <button
                        type="button"
                        onClick={() => setFormData({...formData, role: UserRole.SUPPORT_USER})}
                        className={`p-4 rounded-2xl text-left ${formData.role === UserRole.SUPPORT_USER ? 'orion-role-option-selected' : 'orion-role-option'}`}
                    >
                        <div className="text-xs font-black text-white uppercase mb-1">Support User</div>
                        <div className="text-[10px] text-slate-400 leading-tight">Accès aux clients CRM, impersonation, mais pas de suppression.</div>
                    </button>
                    <button
                        type="button"
                        onClick={() => setFormData({...formData, role: UserRole.SUPER_ADMIN})}
                        className={`p-4 rounded-2xl text-left ${formData.role === UserRole.SUPER_ADMIN ? 'orion-role-option-selected' : 'orion-role-option'}`}
                    >
                        <div className="text-xs font-black text-white uppercase mb-1">Super Admin</div>
                        <div className="text-[10px] text-slate-400 leading-tight">Accès TOTAL au système, suppression client, gestion équipe.</div>
                    </button>
                 </>
               ) : (
                 <>
                    <button
                        type="button"
                        onClick={() => setFormData({...formData, role: UserRole.USER})}
                        className={`p-4 rounded-2xl text-left ${formData.role === UserRole.USER ? 'orion-role-option-selected' : 'orion-role-option'}`}
                    >
                        <div className="text-xs font-black text-white uppercase mb-1">Utilisateur</div>
                        <div className="text-[10px] text-slate-400 leading-tight">Accès aux leads assignés et planning personnel.</div>
                    </button>
                    <button
                        type="button"
                        onClick={() => setFormData({...formData, role: UserRole.COMPANY_ADMIN})}
                        className={`p-4 rounded-2xl text-left ${formData.role === UserRole.COMPANY_ADMIN ? 'orion-role-option-selected' : 'orion-role-option'}`}
                    >
                        <div className="text-xs font-black text-white uppercase mb-1">Administrateur</div>
                        <div className="text-[10px] text-slate-400 leading-tight">Accès complet, gestion équipe et facturation.</div>
                    </button>
                 </>
               )}
            </div>
          </div>
          
          <div className="flex gap-4 pt-4">
            <button type="button" onClick={onClose} disabled={isSubmitting} className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-widest hover:text-white transition-colors disabled:opacity-50">Annuler</button>
            <button type="submit" disabled={isSubmitting} className="flex-1 py-4 orion-user-btn-primary text-white rounded-2xl text-xs font-black uppercase tracking-widest disabled:opacity-70 flex items-center justify-center gap-2">
               {isSubmitting ? (
                 <>
                   <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                     <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                     <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                   </svg>
                   <span>Envoi...</span>
                 </>
               ) : (
                 "Créer l'utilisateur"
               )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default AddUserModal;
